/**
 * Pure transformation functions for benchmark workflow suite dashboard charts.
 *
 * @module useWorkflowSuiteTransforms
 * @see docs/api/services/benchmark.md
 */

import type { BenchmarkWorkflowTrendPoint } from '../useBenchmarkData';

/** Strip "[Benchmark Source] " or "[Benchmark] " prefix from workflow names. */
export function stripBenchmarkPrefix(name: string): string {
  return name.replace(/^\[Benchmark(?:\s+Source)?\]\s*/i, '');
}

type DateRecord = { date: string; [key: string]: string | number };

/** Format ISO date string (YYYY-MM-DD) to short label (e.g. "Mar 14"). */
export function formatDateLabel(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Sort trends by date ascending. Call once upstream and pass the result
 * to buildRunLabels and chart transforms to avoid re-sorting per chart.
 */
export function sortTrendsByDate(
  trends: BenchmarkWorkflowTrendPoint[]
): BenchmarkWorkflowTrendPoint[] {
  return [...trends].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Build per-run x-axis labels. Single run on a date gets "Mar 15",
 * multiple runs get "Mar 15 #1", "Mar 15 #2", etc.
 * Expects trends pre-sorted by date (via sortTrendsByDate).
 */
function buildRunLabels(sortedTrends: BenchmarkWorkflowTrendPoint[]): Map<string, string> {
  // Collect unique runs ordered by date then benchmarkRunId
  const runOrder: { runId: string; date: string }[] = [];
  const seen = new Set<string>();
  for (const t of sortedTrends) {
    if (!seen.has(t.benchmarkRunId)) {
      seen.add(t.benchmarkRunId);
      runOrder.push({ runId: t.benchmarkRunId, date: t.date });
    }
  }

  // Count runs per date
  const dateRunCounts = new Map<string, number>();
  for (const { date } of runOrder) {
    dateRunCounts.set(date, (dateRunCounts.get(date) ?? 0) + 1);
  }

  // Assign labels
  const labels = new Map<string, string>();
  const dateCounters = new Map<string, number>();
  for (const { runId, date } of runOrder) {
    const total = dateRunCounts.get(date)!;
    const label = formatDateLabel(date);
    if (total === 1) {
      labels.set(runId, label);
    } else {
      const idx = (dateCounters.get(date) ?? 0) + 1;
      dateCounters.set(date, idx);
      labels.set(runId, `${label} #${idx}`);
    }
  }
  return labels;
}

/** Filter trends by optional workflow and run-number selections. */
export function filterTrendsForSelection(
  trends: BenchmarkWorkflowTrendPoint[],
  workflowNameFilter?: string[],
  runNumbers?: number[]
): BenchmarkWorkflowTrendPoint[] {
  const workflowSet =
    workflowNameFilter && workflowNameFilter.length > 0 ? new Set(workflowNameFilter) : undefined;
  return trends.filter(
    (t) =>
      (!workflowSet || workflowSet.has(t.workflowName)) &&
      (!runNumbers || runNumbers.length === 0 || runNumbers.includes(t.runNumber))
  );
}

/** Filter to terminal (completed/failed) runs, with optional workflow and run-number filters. */
function filterTerminal(
  trends: BenchmarkWorkflowTrendPoint[],
  workflowNameFilter?: string[],
  runNumbers?: number[]
): BenchmarkWorkflowTrendPoint[] {
  return filterTrendsForSelection(trends, workflowNameFilter, runNumbers).filter(
    (t) => t.status === 'completed' || t.status === 'failed'
  );
}

/** Pre-computed filter + labels to avoid redundant work across chart transforms. */
interface PreparedChartData {
  filtered: BenchmarkWorkflowTrendPoint[];
  runLabels: Map<string, string>;
}

/** Filter and label trends once; pass the result to all chart transforms. */
export function prepareChartData(
  trends: BenchmarkWorkflowTrendPoint[],
  workflowNameFilter?: string[],
  runNumbers?: number[]
): PreparedChartData {
  const filtered = filterTerminal(trends, workflowNameFilter, runNumbers);
  const runLabels = buildRunLabels(filtered);
  return { filtered, runLabels };
}

/**
 * Group terminal runs per suite run, summing `valueKey` across selected run numbers per workflow.
 * Returns one record per suite run with workflow names as dynamic keys.
 */
function groupByRunWithValue(
  { filtered, runLabels }: PreparedChartData,
  valueKey: 'durationMs' | 'totalTokens',
  divisor: number,
  decimals: number
): DateRecord[] {
  // Sum raw values per (benchmarkRunId, workflowName) across included run numbers
  const runMap = new Map<string, Map<string, number>>();
  for (const t of filtered) {
    const val = t[valueKey];
    if (val === null) continue;

    let workflows = runMap.get(t.benchmarkRunId);
    if (!workflows) {
      workflows = new Map();
      runMap.set(t.benchmarkRunId, workflows);
    }
    const prev = workflows.get(t.workflowName) ?? 0;
    workflows.set(t.workflowName, prev + val);
  }

  // Build records in chronological order, applying divisor + rounding once per final value
  const factor = 10 ** decimals;
  const records: DateRecord[] = [];
  for (const [runId, label] of runLabels) {
    const workflows = runMap.get(runId);
    if (!workflows) continue;
    const record: DateRecord = { date: label };
    for (const [name, rawSum] of workflows) {
      record[name] = Math.round((rawSum / divisor) * factor) / factor;
    }
    records.push(record);
  }
  return records;
}

/** Duration chart data: whole seconds per workflow per suite run, summed across selected run numbers. */
export function transformTrendsForDurationChart(prepared: PreparedChartData): DateRecord[] {
  return groupByRunWithValue(prepared, 'durationMs', 1000, 0);
}

/** Token chart data: millions of tokens per workflow per suite run, summed across selected run numbers. */
export function transformTrendsForTokenChart(prepared: PreparedChartData): DateRecord[] {
  return groupByRunWithValue(prepared, 'totalTokens', 1_000_000, 3);
}

/** Get the distinct run numbers present in the trend data. */
export function getAvailableRunNumbers(trends: BenchmarkWorkflowTrendPoint[]): number[] {
  const s = new Set<number>();
  for (const t of trends) s.add(t.runNumber);
  return Array.from(s).sort((a, b) => a - b);
}

interface RunStackedChartResult {
  data: DateRecord[];
  maxRunNumber: number;
}

/**
 * Status chart data: per-run-number stacks with pass/fail coloring.
 * Each suite run bar has segments for run 1 (bottom), run 2, run 3 (top).
 * Uses mutually-exclusive keys: `runN_pass` and `runN_fail`.
 */
export function transformTrendsForStatusChart({
  filtered,
  runLabels,
}: PreparedChartData): RunStackedChartResult {
  // Group by (benchmarkRunId, runNumber) → count pass/fail
  const runMap = new Map<string, Map<number, { pass: number; fail: number }>>();
  let maxRunNumber = 0;

  for (const t of filtered) {
    maxRunNumber = Math.max(maxRunNumber, t.runNumber);

    let byRun = runMap.get(t.benchmarkRunId);
    if (!byRun) {
      byRun = new Map();
      runMap.set(t.benchmarkRunId, byRun);
    }

    let entry = byRun.get(t.runNumber);
    if (!entry) {
      entry = { pass: 0, fail: 0 };
      byRun.set(t.runNumber, entry);
    }
    if (t.status === 'completed') entry.pass += 1;
    if (t.status === 'failed') entry.fail += 1;
  }

  const data: DateRecord[] = [];
  for (const [runId, label] of runLabels) {
    const byRun = runMap.get(runId);
    if (!byRun) continue;
    const record: DateRecord = { date: label, benchmarkRunId: runId };
    for (let n = 1; n <= maxRunNumber; n++) {
      const entry = byRun.get(n);
      record[`run${n}_pass`] = entry?.pass ?? 0;
      record[`run${n}_fail`] = entry?.fail ?? 0;
    }
    data.push(record);
  }

  return { data, maxRunNumber };
}

/**
 * Node results chart data: per-run-number stacks with success/fail/skipped.
 * Each suite run bar has segments for each run number showing node-level detail.
 * Skipped nodes = totalNodes - successfulNodes - failedNodes.
 */
export function transformTrendsForNodeChart({
  filtered,
  runLabels,
}: PreparedChartData): RunStackedChartResult {
  // Group by (benchmarkRunId, runNumber) → sum node counts
  const runMap = new Map<string, Map<number, { success: number; fail: number; skipped: number }>>();
  let maxRunNumber = 0;

  for (const t of filtered) {
    if (t.totalNodes === null) continue;
    maxRunNumber = Math.max(maxRunNumber, t.runNumber);

    let byRun = runMap.get(t.benchmarkRunId);
    if (!byRun) {
      byRun = new Map();
      runMap.set(t.benchmarkRunId, byRun);
    }

    let entry = byRun.get(t.runNumber);
    if (!entry) {
      entry = { success: 0, fail: 0, skipped: 0 };
      byRun.set(t.runNumber, entry);
    }
    const successful = t.successfulNodes ?? 0;
    const failed = t.failedNodes ?? 0;
    entry.success += successful;
    entry.fail += failed;
    entry.skipped += Math.max(0, t.totalNodes - successful - failed);
  }

  const data: DateRecord[] = [];
  for (const [runId, label] of runLabels) {
    const byRun = runMap.get(runId);
    if (!byRun) continue;
    const record: DateRecord = { date: label, benchmarkRunId: runId };
    for (let n = 1; n <= maxRunNumber; n++) {
      const entry = byRun.get(n);
      record[`run${n}_success`] = entry?.success ?? 0;
      record[`run${n}_fail`] = entry?.fail ?? 0;
      record[`run${n}_skipped`] = entry?.skipped ?? 0;
    }
    data.push(record);
  }

  return { data, maxRunNumber };
}

interface CacheCounts {
  cached: number;
  l2Recovery: number;
  ejected: number;
  agent: number;
  skipped: number;
}

/**
 * Cache outcome chart data: per-run-number stacks showing cached, l2 recovery,
 * ejected, and agent steps. Gives a full picture of cache effectiveness per run.
 */
export function transformTrendsForCachedStepsChart({
  filtered,
  runLabels,
}: PreparedChartData): RunStackedChartResult {
  const runMap = new Map<string, Map<number, CacheCounts>>();
  let maxRunNumber = 0;

  for (const t of filtered) {
    if (t.cachedSteps === null) continue;
    maxRunNumber = Math.max(maxRunNumber, t.runNumber);

    let byRun = runMap.get(t.benchmarkRunId);
    if (!byRun) {
      byRun = new Map();
      runMap.set(t.benchmarkRunId, byRun);
    }

    let entry = byRun.get(t.runNumber);
    if (!entry) {
      entry = { cached: 0, l2Recovery: 0, ejected: 0, agent: 0, skipped: 0 };
      byRun.set(t.runNumber, entry);
    }
    entry.cached += t.cachedSteps ?? 0;
    entry.l2Recovery += t.l2RecoverySteps ?? 0;
    entry.ejected += t.ejectedSteps ?? 0;
    entry.agent += t.agentSteps ?? 0;
    entry.skipped += t.skippedSteps ?? 0;
  }

  const data: DateRecord[] = [];
  for (const [runId, label] of runLabels) {
    const byRun = runMap.get(runId);
    if (!byRun) continue;
    const record: DateRecord = { date: label, benchmarkRunId: runId };
    for (let n = 1; n <= maxRunNumber; n++) {
      const entry = byRun.get(n);
      record[`run${n}_cached`] = entry?.cached ?? 0;
      record[`run${n}_l2Recovery`] = entry?.l2Recovery ?? 0;
      record[`run${n}_ejected`] = entry?.ejected ?? 0;
      record[`run${n}_agent`] = entry?.agent ?? 0;
      record[`run${n}_skipped`] = entry?.skipped ?? 0;
    }
    data.push(record);
  }

  return { data, maxRunNumber };
}

/** Sorted unique workflow names from trends data. */
export function getUniqueWorkflowNames(trends: BenchmarkWorkflowTrendPoint[]): string[] {
  return [...new Set(trends.map((t) => t.workflowName))].sort();
}

/** Aggregate KPIs across all trend points. */
export function computeKPIs(trends: BenchmarkWorkflowTrendPoint[]): {
  totalSuites: number;
  successRate: number;
  avgDurationSec: number;
  avgTokens: number;
} {
  const uniqueSuiteIds = new Set(trends.map((t) => t.benchmarkRunId));
  const completed = trends.filter((t) => t.status === 'completed');
  const failed = trends.filter((t) => t.status === 'failed');
  const total = completed.length + failed.length;

  const durations = completed.filter((t) => t.durationMs !== null);
  const tokens = completed.filter((t) => t.totalTokens !== null);

  return {
    totalSuites: uniqueSuiteIds.size,
    successRate: total > 0 ? completed.length / total : 0,
    avgDurationSec:
      durations.length > 0
        ? durations.reduce((sum, t) => sum + (t.durationMs ?? 0) / 1000, 0) / durations.length
        : 0,
    avgTokens:
      tokens.length > 0
        ? tokens.reduce((sum, t) => sum + (t.totalTokens ?? 0), 0) / tokens.length
        : 0,
  };
}

/** Format milliseconds to "Xm Ys" display string. */
export function formatDuration(ms: number | null): string {
  if (ms === null) return '\u2014';
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

/** Format token count with K/M suffixes. */
export function formatTokenCount(tokens: number | null): string {
  if (tokens === null) return '\u2014';
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K`;
  return String(tokens);
}
